import { useEffect, useMemo, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import {
  Bot, Wrench, ExternalLink, LogOut, Zap, Cpu, MessageSquare, Calendar,
  BarChart3, Search as SearchIcon, Rocket, Globe, PlusCircle, Sparkles,
} from "lucide-react";
import { SHOWCASE } from "@/config/brand";
import { CLIENTS_SESSION_KEY } from "@/pages/ClientsLogin";
import BotLauncherDrawer from "@/pages/BotLauncherDrawer";
import OnboardCompanyModal from "@/pages/OnboardCompanyModal";
import CompanyAnalyticsDrawer from "@/pages/CompanyAnalyticsDrawer";

const NEON = "#22C55E";
const NEON_HI = "#4ADE80";
const GOLD = "#F5B800";
const INK = "#0A0A0C";
const PANEL = "#0F0F13";
const LINE = "rgba(255,255,255,0.08)";
const DIM = "rgba(255,255,255,0.6)";
const FADE = "rgba(255,255,255,0.4)";

// Tools available to launch from the hub. These currently open the relevant
// public/admin surface — later they can hit backend orchestration endpoints.
const TOOLS = [
  { key: "salesbot",   icon: Bot,          label: "SalesBot",         desc: "Consultative AI · qualifies + closes", tint: NEON },
  { key: "supportbot", icon: MessageSquare,label: "Support Bot",      desc: "24/7 policy-trained answering",       tint: "#3B82F6" },
  { key: "scheduler",  icon: Calendar,     label: "Scheduling",       desc: "Two-way calendar + SMS reminders",    tint: "#A78BFA" },
  { key: "seo",        icon: SearchIcon,   label: "SEO Engine",       desc: "Schema · sitemaps · programmatic",     tint: GOLD },
  { key: "analytics",  icon: BarChart3,    label: "Analytics",        desc: "Traffic · conversion · attribution",  tint: "#F97316" },
  { key: "automation", icon: Zap,          label: "Automation",       desc: "Email + SMS drip · triggers",         tint: "#22D3EE" },
];

export default function ClientsHub() {
  const [session, setSession] = useState(() => {
    try {
      const raw = localStorage.getItem(CLIENTS_SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [query, setQuery] = useState("");
  const [launched, setLaunched] = useState(null); // { company, tool } — toast for non-bot tools
  const [botCompany, setBotCompany] = useState(null); // active bot drawer target
  const [analyticsCompany, setAnalyticsCompany] = useState(null); // active analytics drawer target
  const [onboardOpen, setOnboardOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    document.title = "Client Hub · Nari AI Operator Console";
  }, []);

  const companies = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SHOWCASE;
    return SHOWCASE.filter((c) =>
      c.name.toLowerCase().includes(q) || c.industry.toLowerCase().includes(q)
    );
  }, [query]);

  const stats = useMemo(() => {
    const live = SHOWCASE.filter((c) => !c.conceptOnly).length;
    const concepts = SHOWCASE.filter((c) => c.conceptOnly).length;
    return { total: SHOWCASE.length, live, concepts };
  }, []);

  if (!session) return <Navigate to="/clients/login" replace />;

  const signOut = () => {
    try { localStorage.removeItem(CLIENTS_SESSION_KEY); } catch { /* ignore */ }
    nav("/clients", { replace: true });
  };

  const launchTool = (company, tool) => {
    // Bot launch → open the real BotProviderAI chat drawer, but only when a
    // specific company is targeted (per-company Bot button). Top-rail
    // launches (no slug) fall back to the toast so the user knows to pick one.
    if ((tool.key === "salesbot" || tool.key === "supportbot") && company?.slug) {
      setBotCompany(company);
      return;
    }
    // Toolkit → open the per-company analytics mini-dashboard
    if (tool.key === "tools" && company?.slug) {
      setAnalyticsCompany(company);
      return;
    }
    setLaunched({ company, tool });
    // Simulated launch — auto-dismiss the confirmation
    setTimeout(() => setLaunched(null), 2600);
  };

  return (
    <div
      className="min-h-screen w-full"
      style={{
        background: `radial-gradient(1200px 600px at 20% -10%, rgba(34,197,94,0.12), transparent 60%), radial-gradient(1000px 500px at 90% 0%, rgba(59,130,246,0.10), transparent 55%), ${INK}`,
        color: "#FFFFFF",
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
      data-testid="clients-hub"
    >
      {/* Top bar */}
      <div className="sticky top-0 z-30 backdrop-blur border-b"
           style={{ background: "rgba(10,10,12,0.72)", borderColor: LINE }}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-2 h-2 rounded-full" style={{ background: NEON, boxShadow: `0 0 14px ${NEON}` }} />
            <div className="text-[11px] uppercase tracking-[0.32em] font-black">
              <span style={{ color: "#B8BEC7" }}>Nari</span>
              <span style={{ color: NEON }}>AI</span>
              <span style={{ color: FADE }}> · Operator Console</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-[11px] uppercase tracking-[0.22em] hidden md:block" style={{ color: DIM }}>
              Signed in as <span style={{ color: "#FFF", fontWeight: 900 }}>{session.user}</span>
            </div>
            <Link to="/" className="text-[11px] uppercase tracking-[0.22em] hover:text-white" style={{ color: DIM }} data-testid="hub-public-site">
              plugitai.com
            </Link>
            <button
              type="button"
              onClick={signOut}
              className="inline-flex items-center gap-2 h-9 px-4 rounded-md border text-[10px] uppercase tracking-[0.24em] font-black"
              style={{ borderColor: LINE, color: DIM }}
              data-testid="hub-signout"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pt-12 pb-8">
        <div className="text-[10px] md:text-[11px] uppercase tracking-[0.34em] font-black mb-3" style={{ color: NEON_HI }}>
          One console · every company
        </div>
        <h1 className="font-black leading-[1.02] tracking-[-0.02em]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.4rem)", fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}
            data-testid="hub-heading">
          Welcome back, <span style={{ color: NEON, textShadow: `0 0 24px ${NEON}66` }}>Operator.</span>
        </h1>
        <p className="mt-3 text-sm md:text-base max-w-[640px]" style={{ color: DIM }}>
          Launch a bot, spin up a tool, or jump into any company site — every module wired into one hub.
        </p>

        {/* Stat pills */}
        <div className="mt-8 grid grid-cols-3 md:flex md:flex-wrap gap-3">
          <StatPill label="Companies" value={stats.total} tint={NEON} />
          <StatPill label="Live sites" value={stats.live} tint="#4CC3FF" />
          <StatPill label="Concept builds" value={stats.concepts} tint={GOLD} />
          <StatPill label="Bots ready" value={TOOLS.length} tint="#A78BFA" />
        </div>
      </section>

      {/* Tool launcher rail */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-8">
        <div className="flex items-center justify-between mb-4">
          <div className="text-[11px] uppercase tracking-[0.28em] font-black" style={{ color: FADE }}>
            <Cpu size={12} className="inline mr-2 -mt-0.5" />Launch a tool
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {TOOLS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => launchTool({ name: "All companies" }, t)}
                className="group text-left rounded-lg border p-4 transition-all"
                style={{ background: PANEL, borderColor: LINE }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = `${t.tint}66`;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 20px 40px -20px ${t.tint}55`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = LINE;
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
                data-testid={`hub-tool-${t.key}`}
              >
                <Icon size={18} style={{ color: t.tint }} />
                <div className="mt-3 font-black text-sm">{t.label}</div>
                <div className="mt-1 text-[11px]" style={{ color: FADE }}>{t.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Companies grid */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] font-black" style={{ color: FADE }}>
              <Globe size={12} className="inline mr-2 -mt-0.5" />Portfolio
            </div>
            <h2 className="mt-1 font-black text-xl md:text-2xl" style={{ fontFamily: "'Cabinet Grotesk','Inter',system-ui,sans-serif" }}>
              Every company you run.
            </h2>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search companies or industries…"
            className="h-11 w-full md:w-80 rounded-md px-4 text-sm outline-none border"
            style={{ background: PANEL, borderColor: LINE, color: "#FFF" }}
            data-testid="hub-search"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companies.map((c) => (
            <CompanyCard key={c.slug} c={c} onLaunch={(tool) => launchTool(c, tool)} />
          ))}
          {/* Add-a-company card */}
          <div className="rounded-xl border border-dashed p-6 flex flex-col items-start justify-between min-h-[280px]"
               style={{ borderColor: LINE, background: "rgba(255,255,255,0.02)" }}
               data-testid="hub-add-company">
            <div>
              <PlusCircle size={22} style={{ color: NEON }} />
              <div className="mt-3 font-black text-base">Onboard a new company</div>
              <div className="mt-1 text-[12px] leading-relaxed" style={{ color: FADE }}>
                Provision a full stack — website, CRM, bots, scheduling, SEO — from one form.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOnboardOpen(true)}
              className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-md text-[10px] uppercase tracking-[0.22em] font-black"
              style={{ background: NEON, color: "#0A0A0C" }}
              data-testid="hub-add-company-cta"
            >
              Start onboarding →
            </button>
          </div>
        </div>
      </section>

      {/* Bottom mini-status bar */}
      <div className="border-t mt-8" style={{ borderColor: LINE }}>
        <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] uppercase tracking-[0.28em]"
             style={{ color: FADE }}>
          <span>Nariai Core · v2026.2 · all systems nominal</span>
          <span>© {new Date().getFullYear()} PlugitAI · Powered by Nariai</span>
        </div>
      </div>

      {/* Launched toast */}
      {launched && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 rounded-lg border px-5 py-3 flex items-center gap-3 shadow-2xl animate-fadeUp"
             style={{ background: "#0A0A0C", borderColor: `${launched.tool.tint}77`, boxShadow: `0 20px 60px -20px ${launched.tool.tint}77` }}
             data-testid="hub-launched-toast">
          <Sparkles size={16} style={{ color: launched.tool.tint }} />
          <div className="text-[12px]">
            <span className="font-black uppercase tracking-[0.2em]" style={{ color: launched.tool.tint }}>Launched</span>
            <span className="mx-2" style={{ color: FADE }}>·</span>
            <span className="text-white font-bold">{launched.tool.label}</span>
            <span className="mx-2" style={{ color: FADE }}>for</span>
            <span className="text-white">{launched.company.name}</span>
          </div>
        </div>
      )}

      {/* Live BotProviderAI drawer for a specific company */}
      {botCompany && (
        <BotLauncherDrawer company={botCompany} onClose={() => setBotCompany(null)} />
      )}

      {/* Per-company analytics mini-dashboard */}
      {analyticsCompany && (
        <CompanyAnalyticsDrawer company={analyticsCompany} onClose={() => setAnalyticsCompany(null)} />
      )}

      {/* Onboard new company modal */}
      {onboardOpen && (
        <OnboardCompanyModal onClose={() => setOnboardOpen(false)} />
      )}

      <style>{`
        @keyframes fadeUp { from { opacity: 0; transform: translate(-50%, 14px);} to { opacity:1; transform: translate(-50%,0);} }
        .animate-fadeUp { animation: fadeUp 340ms cubic-bezier(0.16,1,0.3,1) both; }
      `}</style>
    </div>
  );
}

function StatPill({ label, value, tint }) {
  return (
    <div className="rounded-lg border px-4 py-3 min-w-[130px]"
         style={{ background: PANEL, borderColor: LINE }}
         data-testid={`hub-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: FADE }}>{label}</div>
      <div className="font-black text-xl mt-1" style={{ color: tint, textShadow: `0 0 20px ${tint}55` }}>{value}</div>
    </div>
  );
}

function CompanyCard({ c, onLaunch }) {
  const [imgOk, setImgOk] = useState(true);
  const hasSite = !!c.url && !c.conceptOnly;
  return (
    <div className="rounded-xl border overflow-hidden flex flex-col group"
         style={{ background: PANEL, borderColor: LINE, minHeight: 280 }}
         data-testid={`hub-company-${c.slug}`}>
      <div className="relative aspect-[16/9] overflow-hidden" style={{ background: "#08080A" }}>
        {imgOk && c.screenshotMobile ? (
          <img src={c.screenshotMobile}
               alt={c.name}
               className="w-full h-full object-cover"
               onError={() => setImgOk(false)}
               style={{ filter: "saturate(1.05) contrast(1.02)" }} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-xs uppercase tracking-[0.24em]"
               style={{ color: FADE, background: `linear-gradient(135deg, ${c.accent}22, transparent 60%), #0A0A0C` }}>
            {c.name}
          </div>
        )}
        <div className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.28em] font-black px-2 py-1 rounded"
             style={{ background: "rgba(0,0,0,0.55)", color: c.accent, border: `1px solid ${c.accent}66` }}>
          {c.conceptOnly ? "Concept" : "Live"}
        </div>
      </div>

      <div className="p-4 flex-1 flex flex-col">
        <div className="font-black text-base leading-tight">{c.name}</div>
        <div className="mt-1 text-[11px]" style={{ color: FADE }}>{c.industry}</div>

        <div className="mt-4 grid grid-cols-3 gap-2">
          <button type="button"
                  onClick={() => onLaunch({ key: "salesbot", label: "SalesBot", tint: NEON })}
                  className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md text-[10px] uppercase tracking-[0.18em] font-black"
                  style={{ background: NEON, color: "#0A0A0C" }}
                  data-testid={`hub-launch-bot-${c.slug}`}>
            <Bot size={12} /> Bot
          </button>
          <button type="button"
                  onClick={() => onLaunch({ key: "tools", label: "Toolkit", tint: GOLD })}
                  className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border text-[10px] uppercase tracking-[0.18em] font-black"
                  style={{ borderColor: `${GOLD}55`, color: GOLD }}
                  data-testid={`hub-launch-tools-${c.slug}`}>
            <Wrench size={12} /> Tools
          </button>
          {hasSite ? (
            <a href={c.url} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border text-[10px] uppercase tracking-[0.18em] font-black hover:text-white"
               style={{ borderColor: LINE, color: DIM }}
               data-testid={`hub-open-site-${c.slug}`}>
              <ExternalLink size={12} /> Site
            </a>
          ) : (
            <Link to="/custom"
                  className="inline-flex items-center justify-center gap-1.5 h-9 rounded-md border text-[10px] uppercase tracking-[0.18em] font-black hover:text-white"
                  style={{ borderColor: LINE, color: DIM }}
                  data-testid={`hub-build-site-${c.slug}`}>
              <Rocket size={12} /> Build
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
